import { apiClient } from '@/services/Axios';
import {
  v1Delete,
  v1Get,
  v1Patch,
  v1Post,
  v1Put,
  toLegacyDetail,
  toLegacyPaginated,
  unwrapV1,
} from '@/services/v1Api';

export const CLASSIFICATIONS_V1_BASE = '/api/v1/company/classifications';

/**
 * @param {string} segment
 * @param {Record<string, unknown>} [params]
 * @returns {Promise<unknown[]>}
 */
export async function fetchClassificationList(segment, params = {}) {
  const { data, meta } = await v1Get(`${CLASSIFICATIONS_V1_BASE}/${segment}`, params);
  if (Array.isArray(data)) {
    return data;
  }
  if (data && typeof data === 'object' && Array.isArray(data.data)) {
    return data.data;
  }
  return toLegacyPaginated([], meta).data;
}

/**
 * @param {Record<string, unknown>} [params]
 */
export async function fetchClassificationStructure(params = {}) {
  const { data } = await v1Get(`${CLASSIFICATIONS_V1_BASE}/structure`, params);
  return toLegacyDetail(data);
}

/**
 * @param {Record<string, unknown>} [params]
 */
export async function fetchClassificationTreeTable(params = {}) {
  const { data } = await v1Get(`${CLASSIFICATIONS_V1_BASE}/tree-table`, params);
  return toLegacyDetail(data);
}

/**
 * @param {Record<string, unknown>} [params]
 */
export async function fetchAvailableClassifications(params = {}) {
  const { data } = await v1Get(`${CLASSIFICATIONS_V1_BASE}/available`, params);
  return toLegacyDetail(data);
}

/**
 * @param {Record<string, unknown>} payload
 */
export async function createClassification(payload) {
  const { data } = await v1Post(CLASSIFICATIONS_V1_BASE, payload);
  return toLegacyDetail(data);
}

/**
 * @param {number|string} id
 * @param {Record<string, unknown>} payload
 */
export async function updateClassification(id, payload) {
  const { data } = await v1Put(`${CLASSIFICATIONS_V1_BASE}/${id}`, payload);
  return toLegacyDetail(data);
}

/**
 * @param {number|string} id
 */
export async function deleteClassification(id) {
  const { data } = await v1Delete(`${CLASSIFICATIONS_V1_BASE}/${id}`);
  return toLegacyDetail(data);
}

/**
 * @param {unknown[]} items
 */
export async function reorderClassifications(items) {
  const { data } = await v1Post(`${CLASSIFICATIONS_V1_BASE}/reorder`, { items });
  return toLegacyDetail(data);
}

/**
 * @param {number|string} classificationId
 * @param {File} file
 */
export async function uploadClassificationImage(classificationId, file) {
  const formData = new FormData();
  formData.append('image', file);
  const response = await apiClient.post(
    `${CLASSIFICATIONS_V1_BASE}/${classificationId}/upload-image`,
    formData,
  );
  return toLegacyDetail(unwrapV1(response).data);
}

/**
 * @param {number|string} classificationId
 */
export async function deleteClassificationImage(classificationId) {
  const { data } = await v1Delete(`${CLASSIFICATIONS_V1_BASE}/${classificationId}/delete-image`);
  return toLegacyDetail(data);
}

/**
 * @param {number|string} classificationId
 * @param {number|string|null} templateId
 */
export async function updateClassificationDefaultTemplate(classificationId, templateId) {
  const { data } = await v1Patch(`${CLASSIFICATIONS_V1_BASE}/${classificationId}/default-template`, {
    default_company_template_id: templateId ?? null,
  });
  return toLegacyDetail(data);
}
